import { ReactElement } from 'react';
import { Outlet } from 'react-router-dom';

import ErrorPage from './pages/Error';
import { M3Button } from './components/M3/M3Button';

import { ReactRenderElement } from './types/types';
import { MetadataProvider } from './providers/metadata/metadata';
import { UserProvider } from './providers/user/user';
import { useAuthForm, useAuthProvider } from './providers/auth/auth';
import { SettingsProvider } from './providers/settings/settings';

type Props = {
  children?: any;
};

export type ProviderWrapper = any;

function DataProviders({
  children,
}: {
  children: ReactRenderElement;
}): ReactElement {
  /**
   * Define all your providers that needed to be accessed throughout the app.
   * Order matters here, first provider will be at the top of hierarchy...
   */
  const providers: ProviderWrapper[] = [
    UserProvider,
    SettingsProvider,
    /**
     * Make sure to metadata provider will be called last
     */
    MetadataProvider,
  ];

  return (
    providers.length
      ? providers.reduceRight(
          (children, Provider: ProviderWrapper) => (
            <Provider>{children}</Provider>
          ),
          children,
        )
      : children
  ) as ReactElement;
}

function App({ children }: Props) {
  const { logout } = useAuthForm();
  const { error: authError } = useAuthProvider();

  if (authError) {
    return (
      <ErrorPage
        title='Authentication Error'
        message={
          <>
            Sorry, we're having trouble authenticating your account. Please try
            logging out and logging back in again using the same browser to
            resolve this issue. Thank you for your patience.
            <br />
            <br />
            <M3Button
              color='primary'
              variant='contained'
              sx={{ width: 120 }}
              onClick={logout}
            >
              Log out
            </M3Button>
          </>
        }
      />
    );
  }

  /**
   * Auth has successfully authenticated the user.
   * Wrapped the Outlet with all the providers defined.
   */
  return (
    <DataProviders>
      <Outlet />
    </DataProviders>
  );
}

export default App;
